"use client";

import type { ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/molecules/ui/Dialog";
import { Kbd, KbdGroup } from "@/components/ui/kbd";

type KeyboardShortcutsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function ShortcutRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 border-b border-slate-100 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <span className="text-sm text-slate-700">{label}</span>
      <div className="flex shrink-0 items-center">{children}</div>
    </div>
  );
}

/**
 * Canvas keyboard reference. ⌥ is Option (Mac); the same physical key is Alt on Windows/Linux.
 */
export function KeyboardShortcutsModal({ open, onOpenChange }: KeyboardShortcutsModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Shortcuts use ⌥ (Option). On Windows/Linux, use the Alt key.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <ShortcutRow label="Open this list">
            <KbdGroup>
              <Kbd aria-label="Option">⌥</Kbd>
              <span className="text-slate-400">+</span>
              <Kbd aria-label="K">K</Kbd>
            </KbdGroup>
          </ShortcutRow>
          <ShortcutRow label="Open Big Idea block">
            <KbdGroup>
              <Kbd aria-label="Option">⌥</Kbd>
              <span className="text-slate-400">+</span>
              <Kbd aria-label="1">1</Kbd>
            </KbdGroup>
          </ShortcutRow>
          <ShortcutRow label="Navigate to another block (while lightbox is open)">
            <div className="flex flex-wrap items-center gap-2">
              <KbdGroup>
                <Kbd aria-label="Option">⌥</Kbd>
                <span className="text-slate-400">+</span>
                <Kbd aria-label="Arrow Left">←</Kbd>
              </KbdGroup>
              <KbdGroup>
                <Kbd aria-label="Option">⌥</Kbd>
                <span className="text-slate-400">+</span>
                <Kbd aria-label="Arrow Right">→</Kbd>
              </KbdGroup>
              <KbdGroup>
                <Kbd aria-label="Option">⌥</Kbd>
                <span className="text-slate-400">+</span>
                <Kbd aria-label="Arrow Up">↑</Kbd>
              </KbdGroup>
              <KbdGroup>
                <Kbd aria-label="Option">⌥</Kbd>
                <span className="text-slate-400">+</span>
                <Kbd aria-label="Arrow Down">↓</Kbd>
              </KbdGroup>
            </div>
          </ShortcutRow>
        </div>
      </DialogContent>
    </Dialog>
  );
}
